/**
 * 模块: 全局AI配置组件
 * 职责: 显示和管理全局用户偏好配置
 * 限制: <150行
 */

import React, { useState, useEffect } from 'react';
import { Form, Select, Input, Button, message, Spin, Space, Typography, Card } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import type {
  UserPreference,
  MainGoal,
  BudgetRange,
  ExperienceLevel,
  TimeExpectation,
} from '../../../types/ai-analysis-types';
import {
  MAIN_GOAL_LABELS,
  BUDGET_RANGE_LABELS,
  EXPERIENCE_LEVEL_LABELS,
  TIME_EXPECTATION_LABELS,
} from '../../../types/ai-analysis-types';
import { getMyPreference, savePreference, parseApiError } from '../../../services/aiAnalysisService';

const { TextArea } = Input;
const { Paragraph } = Typography;

export const GlobalAIConfig: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadPreference = async () => {
      setLoading(true);
      try {
        const data = await getMyPreference();
        form.setFieldsValue(data);
      } catch (error) {
        message.error(`加载配置失败: ${parseApiError(error)}`);
      } finally {
        setLoading(false);
      }
    };
    loadPreference();
  }, [form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await savePreference(values as UserPreference);
      message.success('保存成功');
    } catch (error: any) {
      if (error.errorFields) {
        message.error('请检查表单填写');
      } else {
        message.error(`保存失败: ${parseApiError(error)}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    message.info('已重置表单');
  };

  return (
    <Card bordered={false}>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        配置您的全局目标和需求，AI 将根据这些信息为您提供个性化的分析建议。
      </Paragraph>

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            main_goal: 'beginner' as MainGoal,
            budget_range: 'under_5k' as BudgetRange,
            experience_level: 'beginner' as ExperienceLevel,
            time_expectation: '3_months' as TimeExpectation,
            custom_requirements: '',
          }}
        >
          <Form.Item
            name="main_goal"
            label="主要目标"
            rules={[{ required: true, message: '请选择主要目标' }]}
          >
            <Select<MainGoal>>
              {Object.entries(MAIN_GOAL_LABELS).map(([value, label]) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="budget_range"
            label="预算范围"
            rules={[{ required: true, message: '请选择预算范围' }]}
          >
            <Select<BudgetRange>>
              {Object.entries(BUDGET_RANGE_LABELS).map(([value, label]) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="experience_level"
            label="经验水平"
            rules={[{ required: true, message: '请选择经验水平' }]}
          >
            <Select<ExperienceLevel>>
              {Object.entries(EXPERIENCE_LEVEL_LABELS).map(([value, label]) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="time_expectation"
            label="时间预期"
            rules={[{ required: true, message: '请选择时间预期' }]}
          >
            <Select<TimeExpectation>>
              {Object.entries(TIME_EXPECTATION_LABELS).map(([value, label]) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="custom_requirements"
            label="补充需求（可选）"
            extra="您可以在这里补充说明其他特殊需求或偏好，最多 1000 字"
          >
            <TextArea
              rows={4}
              maxLength={1000}
              showCount
              placeholder="例如：我希望专注于家居品类，更看重产品复购率..."
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
              >
                保存配置
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

